import { useEffect, useState } from 'react'
import { createModal } from '../ModalFactory'
import { AuthContext, useAuth } from 'context/AuthContext'
import { ModalWrapper } from '../ModalWrapper'
import {
    TabStrip,
    TabStripSelectEventArguments,
    TabStripTab,
} from '@progress/kendo-react-layout'
import GeneralForm from './GeneralForm'
import Availability from './Availability'
import SchedulingRules from './SchedulingRules'
import Translation from 'components/Translation'
import { useRouter } from 'next/router'
import { RoleTypes } from 'types/staff'
import Pay from './Pay'

const ModalProvider = createModal(
    AuthContext,
    'StaffModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <span className="circular">Create</span>
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-chevron-left " />
        </span>
    )
)

export const StaffModalOpener = ModalProvider.Opener
export const StaffModalCloser = ModalProvider.Closer

function StaffModal() {
    const router = useRouter()
    const { StaffModal, hasRole } = useAuth()
    const [selected, setSelected] = useState<number>(0)

    // Determine if it's a new staff member (if there is no id in attributes)
    const is_new = !StaffModal?.attributes?.id

    useEffect(() => {
        if (StaffModal.is_open) {
            setSelected(0)
        }
    }, [StaffModal.is_open])

    useEffect(() => {
        if (router?.query?.activeTab) {
            const sel = router?.query?.activeTab as unknown as number
            setSelected(sel)
        } else {
            setSelected(0)
        }
    }, [router.pathname])

    useEffect(() => {
        if (StaffModal?.attributes?.selected) {
            setSelected(StaffModal.attributes.selected as number)
        }
    }, [StaffModal?.attributes?.selected])

    const handleSelect = (e: TabStripSelectEventArguments) => {
        setSelected(e.selected)
    }

    const handlePrevSelect = () => {
        setSelected(selected - 1)
    }

    const handleNextSelect = () => {
        setSelected(selected + 1)
    }

    const handleCloseClick = () => {
        router.replace({
            pathname: router.pathname,
            query: null,
        })
        StaffModal.setAttributes({})
        StaffModal.close()
    }

    const handleSubmitClick = () => {
        StaffModal.setAttributes({
            has_updates: true,
        })
        StaffModal.close()
    }

    return (
        <ModalProvider.Visible>
            <ModalWrapper>
                <div className="bg-white shadow-3xl overflow-x-hidden overflow-y-auto sm:rounded-md m-auto relative border-2 border-gray-100 flex flex-col w-full">
                    <div className="flex px-4 text-gray-500 z-10 pt-4 bg-opacity-30 pb-3 bg-primary-lighter">
                        <div>
                            <ul className="ml-6 text-xs flex gap-1 breadcrumbs text-gray-400">
                                <Translation
                                    render_as="li"
                                    content_key="dashboard"
                                />
                                <Translation
                                    render_as="li"
                                    content_key="staff"
                                />
                            </ul>
                            <span
                                className="inline-block self-center text-lg text-primary-dark"
                                onClick={handleCloseClick}
                            >
                                <StaffModalCloser className="self-center" />
                                <span className="circular">
                                    {StaffModal.attributes?.id ? (
                                        `${StaffModal.attributes.first_name} ${StaffModal.attributes.last_name}`
                                    ) : (
                                        <Translation content_key="new_staff" />
                                    )}
                                </span>
                            </span>
                        </div>
                    </div>
                    <TabStrip
                        selected={selected}
                        onSelect={handleSelect}
                        className="z-20"
                        animation={false}
                    >
                        <TabStripTab
                            title={<Translation content_key="general" />}
                        >
                            <div className="p-5 w-full">
                                <GeneralForm
                                    onCloseClick={handleCloseClick}
                                    onSubmitClick={handleSubmitClick}
                                />
                            </div>
                        </TabStripTab>

                        {
                            (hasRole([RoleTypes.SUPER_ADMIN, RoleTypes.ADMIN, RoleTypes.LOCATION_MANAGER]) && !is_new) &&
                            <TabStripTab
                                title={<Translation content_key="pay" />}
                            >
                                <div className="p-5 w-full">
                                    <Pay
                                        onCloseClick={handleCloseClick}
                                        onSubmitClick={handleSubmitClick}
                                    />
                                </div>
                            </TabStripTab>
                        }

                        {
                            !is_new && // Show this only when it's not a new staff member
                            <TabStripTab
                                title={<Translation content_key="availability_col_header" />}
                                contentClassName="w-full"
                            >
                                <div className="p-5 w-full">
                                    <Availability
                                        onCloseClick={handleCloseClick}
                                    />
                                </div>
                            </TabStripTab>
                        }

                        {
                            !is_new && // Show this only when it's not a new staff member
                            <TabStripTab
                                title={<Translation content_key="scheduling_rules" />}
                            >
                                <div className="p-5 w-full">
                                    <SchedulingRules
                                        onPrevSelect={handlePrevSelect}
                                    />
                                </div>
                            </TabStripTab>
                        }

                    </TabStrip>


                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}

export default StaffModal
